import { SelectItem } from "components/molecules/MultiSelectFreeSolo";
import TagsSidebar, { SidebarTagProps } from "components/templates/TagsSidebar";
import { PaginatedResultType } from "graphqlBase/MappingAssetTag/__generated__/getMappingAssetTags";
import { GetMappingDeviceTagsQuery } from "graphqlBase/MappingDeviceTag/__generated__/getMappingDeviceTags";
import { TagCreateType } from "graphqlBase/types";
import getTestIDs from "lib/utils/getTestIDs";
import React from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import useHandleUpdateDeviceTags from "./hooks/useHandleUpdateDeviceTags";
import {
  deviceTagIdsToDeleteAtom,
  deviceTagsforTagsListAtom,
  deviceTagsToCreateAtom,
} from "./recoilState/deviceTagsState";
import { assignedDeviceTagErrorAtom } from "./recoilState/tagModalState";
import translations from "translations";

export const testIDs = getTestIDs();

export type MappingDeviceTag = PaginatedResultType<GetMappingDeviceTagsQuery["mappingDeviceTags"]>;

const DeviceTagsSidebar: React.FC<{
  afterMutation: () => Promise<void>;
  loading: boolean;
  onClose: () => void;
  open: boolean;
}> = ({ open = false, onClose, afterMutation, loading }) => {
  const [deviceTagsForTagsList, setDeviceTagsForTagsList] = useRecoilState(deviceTagsforTagsListAtom);
  const [assignedTagError, setAssignedTagError] = useRecoilState(assignedDeviceTagErrorAtom);
  const setCreateDeviceTags = useSetRecoilState(deviceTagsToCreateAtom);
  const setTagIdsToDelete = useSetRecoilState(deviceTagIdsToDeleteAtom);

  const handleClose = () => {
    setDeviceTagsForTagsList(undefined);
    onClose();
  };

  const { handleAddAndDelete, tagModalLoading, deviceTagsData, handleCloseTagsModal } = useHandleUpdateDeviceTags({
    afterMutation,
    onClose: handleClose,
    loading,
    open,
  });

  const tagOptions =
    deviceTagsData?.tags?.items?.reduce<SelectItem[]>((tagOptions, current) => {
      return tagOptions.concat({ label: current.tagValue, value: current.id });
    }, []) ?? [];

  const handleCloseToast = () => {
    setAssignedTagError(undefined);
  };

  const compareIgnoreCase = (str1: string, str2: string) => str1.toLowerCase() === str2.toLowerCase();

  const createTagsCB = (value: SidebarTagProps[]) => {
    const result = value.reduce((acc: TagCreateType[], curr) => {
      const existingTags = tagOptions?.filter((deviceTag) => compareIgnoreCase(deviceTag.label, curr?.label ?? ""));

      // Create tag if it's new (no ID from database) and doesn't already exist in options
      if (!existingTags?.length && curr?.label?.trim()) {
        const tagValue = curr.label;
        return acc.concat({ tagValue });
      }
      return acc;
    }, []);
    setCreateDeviceTags(result);
  };

  const handleAddTag = (tag: SidebarTagProps) => {
    const newDeviceTagsForTagsList = [...(deviceTagsForTagsList ?? []), tag];
    setDeviceTagsForTagsList(newDeviceTagsForTagsList);
  };

  const handleCreateTag = (tag: string) => {
    if (tag?.trim()?.length) {
      const compareTagLabel = (existingTag: SidebarTagProps) => {
        return compareIgnoreCase(tag, existingTag?.label ?? "");
      };

      const existingTagInList = (deviceTagsForTagsList ?? []).find(compareTagLabel);

      if (!existingTagInList) {
        const tagObject: SidebarTagProps = {
          label: tag,
        };
        const newDeviceTagsForTagsList = [...(deviceTagsForTagsList ?? []), tagObject];
        setDeviceTagsForTagsList(newDeviceTagsForTagsList);
        createTagsCB(newDeviceTagsForTagsList);
      }
    }
  };

  const handleDeleteTag = (item: SidebarTagProps) => {
    if (tagModalLoading) return;
    const tagToRemove = (deviceTagsForTagsList ?? []).find((tag) => {
      return tag.label === item.label;
    });

    if (tagToRemove) {
      setDeviceTagsForTagsList((prevTags) => (prevTags ?? []).filter((tag) => tag !== tagToRemove));
      // Check if tag has an ID (existing tag from database)
      const tagValue = tagToRemove.id;
      if (tagValue !== undefined) {
        setTagIdsToDelete((prev) => [...prev, tagValue ?? ""]);
      }
    }
  };

  return (
    <TagsSidebar
      selectedTags={deviceTagsForTagsList ?? []}
      options={tagOptions}
      handleCancel={handleCloseTagsModal}
      handleSave={handleAddAndDelete}
      handleDeleteTag={handleDeleteTag}
      handleAddTag={handleAddTag}
      handleCreateTag={handleCreateTag}
      open={open}
      disabled={tagModalLoading}
      loading={tagModalLoading}
      title={translations.pages.deviceManagement.deviceTags.updateTags}
      handleCloseToast={handleCloseToast}
      error={assignedTagError}
    />
  );
};

export default DeviceTagsSidebar;
